import { LessonCard } from "@app/grades/components";
import { type Lesson } from "@core/grades/types";
import { useIsMobile } from "@core/shared/hooks";
import { Carousel } from "@design-system/components/Carousel/Carousel";
import { BookOpenIcon } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";

type LessonsCarouselProps = {
  title: string;
  lessons: Lesson[];
  className?: string;
  icon?: React.ReactNode;
};

function isRTL() {
  if (typeof window !== "undefined") {
    return document.dir === "rtl";
  }
  return false;
}

export function LessonsCarousel({
  title,
  lessons,
  className,
  icon = <BookOpenIcon className="h-5 w-5 text-primary-600 dark:text-primary-400 sm:h-6 sm:w-6" />,
}: LessonsCarouselProps) {
  const isMobile = useIsMobile();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: lessons.length > 3,
    slidesToScroll: 1,
    containScroll: "trimSnaps",
    dragFree: false,
    direction: isRTL() ? "rtl" : "ltr",
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  const rtl = isRTL();

  if (!lessons?.length) return null;

  return (
    <section className={className}>
      {lessons.length > 3 ? (
        <>
          {/* Mobile: Arrows beside title */}
          {isMobile ? (
            <div className="mb-6 flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-3">
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 mt-0.5">
                  {icon}
                </div>
                <h2 className="font-heading text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100 leading-tight min-w-0">
                  {title}
                </h2>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={rtl ? scrollNext : scrollPrev}
                  disabled={rtl ? !canScrollNext : !canScrollPrev}
                  className="rounded-full bg-gray-100 dark:bg-gray-800 p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-40"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={rtl ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} />
                  </svg>
                </button>
                <button
                  onClick={rtl ? scrollPrev : scrollNext}
                  disabled={rtl ? !canScrollPrev : !canScrollNext}
                  className="rounded-full bg-gray-100 dark:bg-gray-800 p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-40"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={rtl ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            <SectionHeading title={title} icon={icon} />
          )}
          <div className="relative flex w-full items-center gap-2">
            {/* Desktop arrows outside carousel */}
            {!isMobile && (
              <button
                onClick={rtl ? scrollNext : scrollPrev}
                disabled={rtl ? !canScrollNext : !canScrollPrev}
                className="z-10 rounded-full bg-white dark:bg-gray-800 p-1.5 text-gray-700 dark:text-gray-300 shadow hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={rtl ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} />
                </svg>
              </button>
            )}
            <div className="embla__viewport w-full overflow-x-hidden py-1" ref={emblaRef}>
              <div className="embla__container flex">
                {lessons.map((lesson, idx) => (
                  <div
                    className="embla__slide min-w-0 flex-none px-2"
                    style={{ width: `${100 / (isMobile ? 1 : 3)}%` }}
                    key={lesson.id}
                  >
                    <LessonCard lesson={lesson} />
                  </div>
                ))}
              </div>
            </div>
            {!isMobile && (
              <button
                onClick={rtl ? scrollPrev : scrollNext}
                disabled={rtl ? !canScrollPrev : !canScrollNext}
                className="z-10 rounded-full bg-white dark:bg-gray-800 p-1.5 text-gray-700 dark:text-gray-300 shadow hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={rtl ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
                </svg>
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <SectionHeading title={title} icon={icon} />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {lessons.map((lesson) => (
              <LessonCard key={lesson.id} lesson={lesson} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
